import { useEffect, useMemo, useState } from "react";
import { Sparkles, X, TrendingUp, Zap, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { useValUsage } from "@/hooks/useValUsage";
import { useS360Tier, S360_TIER_LABELS } from "@/hooks/useS360Tier";
import { useTenant } from "@/context/TenantContext";
import ValRecargaDialog from "@/components/common/ValRecargaDialog";
import { addNotif } from "@/lib/notificacoesStore";

/**
 * UpgradeBanner (Phase 11.12) — Alerta de consumo Val.
 *
 * Renderizado no topo do app (abaixo do Header) SEMPRE que o consumo do
 * mês corrente atinge o threshold configurado pelo usuário (default 70%).
 *
 * Dados 100% REAIS: `useValUsage()` → /api/val/usage/{workspace_id}.
 *
 * Elementos:
 *   • Ícone Sparkles com cor por severidade (âmbar ≥threshold, vermelho ≥90%).
 *   • Título "ATENÇÃO · CONSUMO ELEVADO" (ou "LIMITE QUASE ESGOTADO" se ≥90%).
 *   • Texto: "Você já usou X% da sua cota de IA Val do plano {tier}".
 *   • "restam {tokens} tokens · fecha em {N} dias" (dias reais até fim do mês).
 *   • Botão pill "% ATUAL" (o antigo indicador do header, mesma cor/família).
 *   • Botão "Comprar créditos" → abre ValRecargaDialog.
 *   • Botão engrenagem → popover para o usuário ajustar o threshold (30-95%).
 *   • Botão X para dispensar (persiste por sessão).
 */

const LS_THRESHOLD = "val_alert_threshold_v1";
const LS_DISMISSED = "val_alert_dismissed_v1"; // "YYYY-MM-DD" — dispensa por dia

function daysUntilMonthEnd() {
  const now = new Date();
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return Math.max(0, Math.ceil((last.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
}

function readThreshold() {
  try {
    const raw = localStorage.getItem(LS_THRESHOLD);
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 30 && n <= 95) return n;
  } catch { /* noop */ }
  return 70;
}

function readDismissed() {
  try { return localStorage.getItem(LS_DISMISSED) || ""; } catch { return ""; }
}

export default function UpgradeBanner() {
  const [threshold, setThreshold] = useState(readThreshold);
  const [dismissedAt, setDismissedAt] = useState(readDismissed);
  const [recargaOpen, setRecargaOpen] = useState(false);

  const { corrente, cota, pct, restante, workspaceId, reload } = useValUsage();
  const { tier } = useS360Tier();
  const { planoCustomizado } = useTenant();

  const todayKey = new Date().toISOString().slice(0, 10);
  const wasDismissedToday = dismissedAt === todayKey;

  useEffect(() => {
    try { localStorage.setItem(LS_THRESHOLD, String(threshold)); } catch { /* noop */ }
  }, [threshold]);

  const dias = useMemo(() => daysUntilMonthEnd(), []);
  const tokensRestEst = useMemo(() => {
    // Estimativa simples de tokens restantes: baseia-se na média de tokens/consulta
    // do mês corrente. Se ainda não tem consultas, usa 500 (chute conservador).
    const c = corrente?.consultas || 0;
    const totalTokens = (corrente?.tokens_in || 0) + (corrente?.tokens_out || 0);
    const avg = c > 0 ? totalTokens / c : 500;
    return Math.max(0, Math.round(restante * avg));
  }, [corrente, restante]);

  // Não mostra se abaixo do threshold, se cota 0, se dispensado hoje.
  // E3 — Plano Customizado nunca exibe convite a créditos/planos tradicionais.
  if (planoCustomizado || cota === 0 || pct < threshold || wasDismissedToday) return null;

  const isCritical = pct >= 90;
  const toneWrap = isCritical
    ? "bg-gradient-to-r from-red-50 via-amber-50/60 to-amber-50/40 border-red-200"
    : "bg-gradient-to-r from-amber-50 via-amber-50/60 to-violet-50/40 border-amber-200";
  const toneIconWrap = isCritical ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700";
  const toneLabel = isCritical ? "text-red-700" : "text-amber-700";
  const tonePill = isCritical
    ? "bg-red-100 text-red-700 border-red-200"
    : "bg-amber-100 text-amber-700 border-amber-200";
  const ctaTone = isCritical ? "bg-red-700 hover:bg-red-800" : "bg-amber-600 hover:bg-amber-700";

  const handleDismiss = () => {
    setDismissedAt(todayKey);
    try { localStorage.setItem(LS_DISMISSED, todayKey); } catch { /* noop */ }
    // Phase 11.12 — Grava a mesma info no sino do header para não perder o aviso.
    addNotif({
      tipo: "consumo",
      titulo: isCritical ? "Cota Val quase esgotada" : "Consumo Val elevado",
      descricao: `Você usou ${pct}% da cota do plano ${S360_TIER_LABELS[tier] || tier}. Restam ${restante} consulta${restante === 1 ? "" : "s"} · fecha em ${dias} ${dias === 1 ? "dia" : "dias"}.`,
      tempo: "agora",
      chave_dedup: `val-usage-${todayKey}`,
      cta_href: "/app/subscricoes",
      cta_label: "Ver planos",
    });
  };

  const tokensFmt = tokensRestEst > 1000
    ? `${(tokensRestEst / 1000).toFixed(0)}k`
    : String(tokensRestEst);

  return (
    <>
      <div
        data-testid="upgrade-banner"
        className={cn("relative flex items-center gap-3 px-5 py-2.5 border-b text-sm", toneWrap)}
      >
        <div className={cn("shrink-0 w-7 h-7 rounded-md grid place-items-center", toneIconWrap)}>
          <Sparkles className="w-4 h-4" />
        </div>

        <div className="flex-1 min-w-0 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className={cn("text-[11px] uppercase tracking-wider font-bold", toneLabel)}>
            {isCritical ? "Limite quase esgotado" : "Atenção · consumo elevado"}
          </span>
          <span
            className={cn(
              "inline-flex items-center h-5 px-2 rounded-full border text-[11px] font-bold tabular-nums",
              tonePill,
            )}
            data-testid="upgrade-banner-pct-badge"
            title="Percentual consumido do plano no mês corrente"
          >
            {pct}%
          </span>
          <span className="text-sm text-slate-800 font-medium">
            da sua cota de IA Val do plano <strong>{S360_TIER_LABELS[tier] || tier}</strong>
          </span>
          <span className="text-xs text-slate-500">
            · restam <strong className="tabular-nums text-slate-700">{tokensFmt}</strong> tokens
            · fecha em <strong className="tabular-nums text-slate-700">{dias}</strong> {dias === 1 ? "dia" : "dias"}
          </span>
        </div>

        {/* Ajuste de threshold */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="shrink-0 text-slate-500 hover:text-slate-800 p-1 rounded hover:bg-white/60"
              data-testid="upgrade-banner-config"
              title="Ajustar quando o alerta aparece"
            >
              <Settings2 className="w-4 h-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" sideOffset={6} className="w-72 p-4" data-testid="upgrade-banner-config-pop">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
              Mostrar alerta a partir de
            </div>
            <div className="text-lg font-bold text-slate-900 tabular-nums mb-2">
              {threshold}% de consumo
            </div>
            <Slider
              value={[threshold]}
              min={30}
              max={95}
              step={5}
              onValueChange={(v) => setThreshold(v[0])}
              data-testid="upgrade-banner-threshold-slider"
            />
            <div className="flex justify-between text-[10px] text-slate-400 mt-1 tabular-nums">
              <span>30%</span><span>60%</span><span>95%</span>
            </div>
            <p className="text-[10px] text-slate-500 mt-3 leading-snug">
              O alerta some automaticamente quando o consumo cai abaixo do limite escolhido, e pode ser dispensado (X) para o restante do dia.
            </p>
          </PopoverContent>
        </Popover>

        <Button
          size="sm"
          onClick={() => setRecargaOpen(true)}
          className={cn("shrink-0 gap-1.5 text-white", ctaTone)}
          data-testid="upgrade-banner-cta"
        >
          <Zap className="w-3.5 h-3.5" />
          Comprar créditos
        </Button>

        <button
          onClick={handleDismiss}
          className="shrink-0 text-slate-400 hover:text-slate-700 p-1 rounded hover:bg-white/60"
          data-testid="upgrade-banner-dismiss"
          title="Dispensar até amanhã"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <ValRecargaDialog
        open={recargaOpen}
        onClose={() => setRecargaOpen(false)}
        workspaceId={workspaceId}
        restante={restante}
        onSuccess={() => reload()}
      />
    </>
  );
}
